<template>
  <div v-if="!isHiddenChart" class="ppi-chart">
    <span class="ppi-chart__title">{{ getNameInRussiaRating }}</span>
    <div class="ppi-chart__content" v-if="investmentRussia?.bars?.length">
      <div class="ppi-chart__rating">
        <EChartsColumnAndLine
          v-if="getFdiInData.inRussiaLabels && countryData?.nominativeCase"
          :xData="getFdiInData.inRussiaLabels"
          :columnYData="getFdiInData.inRussiaColumnYData"
          :columnYName="getInRussiaRaitingColumnYName"
          :lineYData="getFdiInData.inRussiaLineYData"
          :lineYName="getInRussiaRaitingLineYName"
          :colors="['#0393BA', '#73EA48']"
          :marker="inRussiaRaitingMarker"
          :stroke="inRussiaRaitingStroke"
          sizeXYLabel="14"
          sizeDataLabel="13"
          :offsetYDataLabel="-10"
          :height="400"
          :formatYLabelFunction="getLeadingInvestorsLabelFormat"
        />
      </div>
      <div class="ppi-chart__info info" v-if="investmentRussia?.position > 0">
        <span class="info__title">
          Среди стран-инвесторов в Россию {{ summary?.countryName || '' }} находится на:
        </span>
        <div class="info__position position" v-if="investmentRussia?.position">
          <span class="position__title">{{ investmentRussia.position }} место</span>
          <span class="position__text">по статистике Банка России</span>
        </div>
        <div class="info__position position" v-if="investmentRussia?.positionAdjusted">
          <span class="position__title position__title--green">{{ investmentRussia.positionAdjusted }} место</span>
          <span class="position__text">
            по статистике Банка России с корректировкой на перечень стран-промежуточных инвесторов
            <Tooltip :triggers="['click']" class="tooltip-component--mobile-big tooltip-component--investment">
              <span class="position__help-text">
                Из рейтинга исключаются офшорные, низконалоговые и промежуточные юрисдикции, определенные в перечнях и
                материалах Минфина и ФНС России, ЮНКТАД и ОЭСР
              </span>
            </Tooltip>
          </span>
        </div>
      </div>
    </div>
    <div class="ppi-chart__noData" v-else>Нет данных</div>
  </div>
</template>

<script>
  import text from '@/common/mixins/text';
  import Tooltip from '@/common/ui/Tooltip.vue';
  import { numberFormatter } from '@/common/utils/numberFormatter';

  import EChartsColumnAndLine from '@/components/ChartComponents/apacheEcharts/EChartsColumnAndLine.vue';

  import Api from '@/modules/countryАnalytics/api';

  export default {
    name: 'PIIChart',

    components: {
      Tooltip,
      EChartsColumnAndLine,
    },

    mixins: [text],

    props: {
      countryId: {
        type: String,
        default: '',
      },
      summary: {
        type: Object,
        default: () => {},
      },
      // Если установить в true, то график будет отображаться только если у страны нет позиции в ПИИ (position = -1)
      isHiddenChartWithPositions: {
        type: Boolean,
        default: false,
      },
      countryData: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      return {
        investmentRussia: {},
        inRussiaRaitingMarker: {
          size: 6,
          colors: ['#73EA48'],
          strokeColors: '',
          strokeWidth: 0,
        },
        inRussiaRaitingStroke: {
          width: [0, 2],
          curve: 'straight',
          dashArray: [0, 5, 0],
        },
        isHiddenChart: false,
      };
    },

    created() {
      this.loadData();
    },

    computed: {
      lineUnit() {
        const values = (this.investmentRussia?.bars ?? [])
          .map((bar) => bar.percent)
          .filter((value) => typeof value === 'number');
        return this.resolveFdiUnit(values);
      },

      getFdiInData() {
        const data = {
          inRussiaLabels: [],
          inRussiaColumnYData: [],
          inRussiaLineYData: [],
        };

        const { factor } = this.lineUnit;

        this.investmentRussia.bars.forEach((item) => {
          data.inRussiaLabels.push(item.year);
          data.inRussiaColumnYData.push(item.value.toFixed(0));
          data.inRussiaLineYData.push(this.toFdiUnitValue(item.percent, factor));
        });

        return data;
      },

      getNameInRussiaRating() {
        return this.investmentRussia?.position > 0
          ? 'ПИИ в Россию, млрд $'
          : `Совокупные ПИИ в ${this.countryData?.accusativeCase}, млрд $`;
      },

      getInRussiaRaitingColumnYName() {
        return this.investmentRussia?.position > 0 ? 'Все страны, млрд $' : 'Запас';
      },

      getInRussiaRaitingLineYName() {
        return this.investmentRussia?.position > 0
          ? `${this.countryData?.nominativeCase}, ${this.lineUnit.label} $`
          : 'Поток (пр. шкала)';
      },
    },

    methods: {
      loadData() {
        this.investmentRussia = {};

        Api.getInvestmentRussia(this.countryId)
          .then((response) => {
            if (response?.data) {
              response.data.bars.sort((a, b) => {
                return Number(a.year) - Number(b.year);
              });
              this.investmentRussia = response.data;
            }
          })
          .catch((err) => {
            console.error(err);
            this.investmentRussia = {};
          });
      },
      getLeadingInvestorsLabelFormat(value) {
        return numberFormatter(value, 'ru-RU', 2);
      },
      resolveFdiUnit(values) {
        const UNITS = [
          { label: 'млрд', factor: 0.001 },
          { label: 'млн', factor: 1 },
          { label: 'тыс', factor: 1000 },
        ];

        const maxAbsValue = Math.max(0, ...values.map((value) => Math.abs(value)));

        for (let i = 0; i < UNITS.length; i++) {
          if (i === UNITS.length - 1 || maxAbsValue * UNITS[i].factor >= 0.5) return UNITS[i];
        }

        return UNITS[UNITS.length - 1];
      },
      toFdiUnitValue(value, factor) {
        if (!value) return 0;
        const rounded = Number((value * factor).toFixed(1));
        if (rounded !== 0) return rounded;
        return value > 0 ? 0.1 : -0.1;
      },
    },

    watch: {
      countryId: function () {
        this.loadData();
      },
      'investmentRussia.position': function (newValue) {
        this.isHiddenChart = this.isHiddenChartWithPositions && newValue > 0;
      },
    },
  };
</script>

<style scoped lang="scss">
  .ppi-chart {
    display: flex;
    flex-direction: column;
    width: 100%;

    &__title {
      font: $fira-20-M;
      color: $base;
      margin: 0 0 24px 0;
    }

    &__content {
      display: flex;
      width: 100%;
      gap: 16px;

      @media screen and (max-width: $tablet) {
        flex-direction: column;
      }
    }

    &__rating {
      flex: 1;
      width: 100%;

      @media screen and (max-width: $tablet) {
        overflow: hidden;
        overflow-x: auto;
      }
    }

    :deep(.chartBar) {
      width: 100%;
    }

    .info {
      display: flex;
      flex-direction: column;
      gap: 16px;
      flex: 1;

      &__title {
        font: $fira-16;
        color: $base;
      }

      .position {
        display: flex;
        flex-direction: column;

        &__title {
          font: $fira-24-M;
          color: $blue;

          &--green {
            color: $green;
          }
        }

        &__text {
          position: relative;
          font: $fira-16;
          color: $base;
          display: inline-block;
        }

        &__help-text {
          margin: 0 16px 0 8px;
        }
      }
    }
  }
</style>
